import { Link, useParams, Navigate } from "react-router-dom";
import {
  Shield, Car, Video, DoorOpen, CalendarCheck, ShoppingBag, HardHat,
  KeyRound, UserCheck, Building2, MapPin, ChevronRight, Dog, Warehouse, Hotel, Building, School, HeartPulse,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { PageHero, SectionHead, FaqBlock, CtaBand, Breadcrumbs } from "@/components/Blocks";
import {
  CITIES, SERVICES, getCity, cityFaqs, comboAnchor, breadcrumbLd, faqLd,
} from "@/data/contentEngine";

const SERVICE_ICONS = {
  "manned-guarding": Shield,
  "mobile-patrols": Car,
  "cctv-monitoring": Video,
  "door-supervision": DoorOpen,
  "event-security": CalendarCheck,
  "retail-security": ShoppingBag,
  "construction-site-security": HardHat,
  "key-holding-alarm-response": KeyRound,
  "close-protection": UserCheck,
  "corporate-security": Building2,
  "k9-security-patrols": Dog,
  "warehouse-industrial-security": Warehouse,
  "hotel-hospitality-security": Hotel,
  "vacant-property-security": Building,
  "school-campus-security": School,
  "hospital-healthcare-security": HeartPulse,
};

const CityPage = () => {
  const { citySlug } = useParams();
  const city = getCity(citySlug);
  if (!city) return <Navigate to="/cities" replace />;

  const faqs = cityFaqs(city);
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      breadcrumbLd([
        { label: "Home", to: "/" },
        { label: "Areas We Cover", to: "/cities" },
        { label: `Security Guards ${city.name}`, to: `/cities/${city.slug}` },
      ]),
      faqLd(faqs),
    ],
  };

  const nearby = city.nearby.map((s) => getCity(s)).filter(Boolean);

  return (
    <>
      <Seo
        title={`Security Guards ${city.name} | SIA Security Company ${city.county} | Xtreme Protection`}
        description={`SIA-licensed security guards in ${city.name}, ${city.county}. Manned guarding, mobile patrols, CCTV monitoring, door supervision & alarm response across ${city.name}. Call +44 2046 402840.`}
        path={`/cities/${city.slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow={`Security Company · ${city.county}`}
        title={`Security Guard Services in ${city.name}`}
        sub={city.intro}
        image={SERVICES[0].image}
        testId="city-hero"
      />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-12" data-testid="city-content">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Areas We Cover", to: "/cities" },
            { label: city.name, to: null },
          ]}
        />
        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <SectionHead
              eyebrow="Local Risk Landscape"
              title={`Security challenges in ${city.name}`}
              testId="city-local-heading"
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-300">{city.local}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <h2 className="mt-12 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="city-hubs-heading">
                Areas of {city.name} we protect
              </h2>
            </Reveal>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="city-hubs-list">
              {city.hubs.map((h) => (
                <span
                  key={h}
                  className="rounded-sm border border-slate-700 bg-obsidian-900 px-3 py-1.5 text-xs text-slate-300"
                >
                  {h}
                </span>
              ))}
            </div>
            <Reveal delay={0.1}>
              <h2 className="mt-14 font-display text-2xl font-bold uppercase tracking-tight text-slate-100" data-testid="city-faq-heading">
                Security in {city.name} — FAQs
              </h2>
            </Reveal>
            <div className="mt-4">
              <FaqBlock faqs={faqs} testId="city-faq" />
            </div>
          </div>
          <aside>
            <Reveal>
              <div className="rounded-md border border-slate-800 bg-obsidian-900 p-6" data-testid="nearby-cities-panel">
                <h2 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-amber-400">
                  Nearby coverage
                </h2>
                <ul className="mt-4 space-y-1.5">
                  {nearby.map((n) => (
                    <li key={n.slug}>
                      <Link
                        to={`/cities/${n.slug}`}
                        data-testid={`nearby-city-${n.slug}`}
                        className="group flex items-center justify-between rounded-sm px-2 py-1.5 text-sm text-slate-300 transition-colors hover:bg-obsidian-800 hover:text-amber-400"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-slate-600 group-hover:text-amber-500" />
                          Security Guards {n.name}
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 text-slate-700 group-hover:text-amber-500" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>

        <div className="mt-16">
          <SectionHead
            eyebrow={`Security Services in ${city.name}`}
            title={`Every service, delivered locally in ${city.name}`}
            sub={`Ten specialist security services available across ${city.name} and ${city.county} — each with a dedicated local page.`}
            testId="city-services-heading"
          />
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => {
              const Icon = SERVICE_ICONS[s.slug];
              return (
                <Reveal key={s.slug} delay={(i % 3) * 0.06}>
                  <Link
                    to={`/services/${s.slug}/${city.slug}`}
                    data-testid={`city-service-link-${s.slug}`}
                    className="tactical-card group flex h-full items-center gap-4 rounded-md p-5"
                  >
                    <Icon className="h-6 w-6 shrink-0 text-amber-500" />
                    <span className="flex-1 font-display text-base font-bold uppercase tracking-wide text-slate-100">
                      {comboAnchor(s, city)}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-slate-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-amber-500" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-12">
        <CtaBand
          title={`Get protected in ${city.name}`}
          sub={`Free risk assessment for any ${city.name} premises — call our control room or email the team for a tailored quotation.`}
        />
      </section>
    </>
  );
};

export default CityPage;
